import styled, { keyframes } from 'styled-components';
import { Row, Card, Table, Button, Tabs, Select, DatePicker } from 'antd';
import { HeartFilled, LoadingOutlined } from '@ant-design/icons';
import { COLORS } from '../../constants/colors';

/* Compact pulsing-heart loader for use as an Ant Table/Spin indicator.
   Kept to a fixed size (no wrapper padding) so Ant centers it in the row. */
const outreachHeartPulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.65; }
  100% { transform: scale(1); opacity: 1; }
`;

export const OutreachHeartLoader = styled(HeartFilled)`
  font-size: 26px;
  color: #1a1a1a;
  animation: ${outreachHeartPulse} 1.4s ease-in-out infinite;
`;

/* Solid black ring spinner (not the default blue dotted Ant Spin). Use as an
   inline loader for deferred counts, e.g. <BlackSpinner style={{ fontSize: 16 }} />. */
const blackSpinnerRotate = keyframes`
  to { transform: rotate(360deg); }
`;

export const BlackSpinner = styled(LoadingOutlined)`
  color: #1a1a1a;
  font-size: 16px;
  animation: ${blackSpinnerRotate} 0.8s linear infinite;
`;

/* Shimmering placeholder bar for skeleton / ghost table rows. */
const shimmerSlide = keyframes`
  0% { background-position: -420px 0; }
  100% { background-position: 420px 0; }
`;

export const Shimmer = styled.div`
  height: 14px;
  border-radius: 6px;
  background: #ececec;
  background-image: linear-gradient(90deg, #ececec 0px, #f6f6f6 60px, #ececec 120px);
  background-size: 600px 100%;
  animation: ${shimmerSlide} 1.4s ease-in-out infinite;
`;

export const PageContainer = styled.div`
  /* No padding - AdminLayout handles it */
  
  /* Animation Keyframes */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Animation Classes */
  .header-animate-in {
    animation: fadeInDown 0.6s ease-out forwards;
    opacity: 0;
  }

  .stats-animate-in {
    animation: fadeInLeft 0.6s ease-out forwards;
    animation-delay: 0.1s;
    opacity: 0;
  }

  .filters-animate-in {
    animation: fadeInLeft 0.6s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }

  .table-animate-in {
    animation: fadeInScale 0.6s ease-out forwards;
    animation-delay: 0.3s;
    opacity: 0;
  }
`;

export const StyledTabs = styled(Tabs)`
  margin: 0 !important;
  background: transparent;
  padding-top: 16px;
  padding-bottom: 8px;
  margin-bottom: 0;
  
  .ant-tabs-nav {
    margin: 0 !important;
    
    &::before {
      display: none !important;
    }
  }
  
  /* Hide ink bar */
  .ant-tabs-ink-bar {
    display: none !important;
  }
  
  /* Make the tab strip horizontally scrollable on small widths */
  .ant-tabs-nav-wrap {
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    
    &::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  }
  
  .ant-tabs-nav-list {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    
    .ant-tabs-tab {
      position: relative;
      padding: 0;
      margin: 0;
      background: transparent;
      border: none;
      transition: all 0.2s ease;
      flex: none;
      width: auto;
      
      /* Remove dividers */
      &::before {
        display: none;
      }
      
      .ant-tabs-tab-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border-radius: 999px;
        color: ${COLORS.textMedium};
        font-weight: 500;
        font-size: 14px;
        transition: background-color .2s ease, color .2s ease;
        background: transparent;
        border: none;
        min-height: 40px;
        white-space: nowrap;
        
        .anticon {
          font-size: 14px;
        }
      }
      
      &:hover .ant-tabs-tab-btn {
        color: ${COLORS.textDark};
        background: rgba(24, 144, 255, 0.08);
      }
      
      &.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${COLORS.$primaryBlue};
        background: #eef4ff;
        font-weight: 600;
        box-shadow: 0 1px 3px rgba(0,0,0,.06);
        border: 1px solid rgba(24, 144, 255, 0.3);
      }
      
      /* Visible keyboard focus */
      .ant-tabs-tab-btn:focus-visible {
        outline: 2px solid ${COLORS.$primaryBlue};
        outline-offset: 2px;
        border-radius: 999px;
      }
    }
  }
`;

export const TabsBorderDiv = styled.div`
  height: 1px;
  background-color: ${COLORS.border};
  margin-bottom: 24px;
`;

export const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 8px;
  border-radius: 10px;
  background-color: ${props => props.color || '#8c8c8c'};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  margin-left: 6px;
  transition: background-color 0.2s ease;
`;

export const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${COLORS.border};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const StatsRow = styled(Row)`
  margin-bottom: 24px;
`;

export const StatCard = styled(Card)`
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  height: 100%;
`;

/* ============================================================
   Outreach page styled components
   ============================================================ */

/* Stat card with a colored icon chip, matching the app's card language */
export const OutreachStatCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .ant-card-body {
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .stat-icon {
    width: 46px;
    height: 46px;
    min-width: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #fff;
    background: ${(props) => props.$accent || COLORS.$primaryBlue};
  }

  .stat-content {
    min-width: 0;

    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: ${COLORS.textDark};
      line-height: 1.2;
    }

    .stat-label {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      white-space: nowrap;
    }
  }
`;

/* Drawer header summary strip (event stats) */
export const DrawerStatsBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .drawer-stat {
    flex: 1;
    min-width: 140px;
    background: #fafbfc;
    border: 1px solid ${COLORS.border};
    border-radius: 12px;
    padding: 14px 16px;

    .drawer-stat-value {
      font-size: 22px;
      font-weight: 700;
      color: ${COLORS.textDark};
      line-height: 1.2;

      &.accent-blue { color: ${COLORS.$primaryBlue}; }
      &.accent-green { color: #52c41a; }
    }

    .drawer-stat-label {
      font-size: 12px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-top: 2px;
    }
  }
`;

/* Toolbar row above tables (selection info + bulk actions) */
export const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;

  .toolbar-hint {
    color: ${COLORS.textMedium};
    font-size: 13px;
  }
`;

/* Perfectly circular row action button. A plain <button> (not Ant's Button)
   so no library sizing rules can stretch it into an oval. */
export const RowActionButton = styled.button.attrs({ type: 'button' })`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  flex: 0 0 32px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${COLORS.border};
  background: #fff;
  color: ${COLORS.textMedium};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 13px;
    display: block;
  }

  &:hover:not(:disabled) {
    color: ${COLORS.$primaryBlue};
    border-color: ${COLORS.$primaryBlue};
    background: rgba(24, 144, 255, 0.06);
  }

  &:disabled {
    color: ${COLORS.textMuted};
    background: #fafafa;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.$primaryBlue};
    outline-offset: 2px;
  }
`;

export const FilterBar = styled.div`
  margin-bottom: 20px;
`;

export const StyledInput = styled.input`
  height: 40px;
  border-radius: 8px;
  border: 1.5px solid ${COLORS.border};
  box-shadow: none;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.2s ease;
  padding: 0 16px;
  width: 100%;
  outline: none;
  
  &:hover {
    border-color: ${COLORS.$primaryBlue};
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
  }
  
  &:focus {
    border-color: ${COLORS.$primaryBlue};
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15);
  }
  
  &::placeholder {
    color: ${COLORS.textMuted};
    font-weight: 400;
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    height: 40px !important;
    min-height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: none !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    padding: 0 16px !important;
    display: flex !important;
    align-items: center !important;
    transition: all 0.2s ease !important;
  }
  
  &:hover .ant-select-selector {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-select-selection-placeholder {
    color: ${COLORS.textMuted};
  }
`;

export const StyledDatePicker = styled(DatePicker.RangePicker)`
  height: 40px;
  border-radius: 8px;
  border: 1.5px solid ${COLORS.border};
  box-shadow: none;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${COLORS.$primaryBlue};
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
  }
  
  &.ant-picker-focused {
    border-color: ${COLORS.$primaryBlue};
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15);
  }
`;

export const CaseTable = styled(Table)`
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    white-space: nowrap !important;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }
  
  .priority-high {
    border-left: 3px solid #ff4d4f;
  }
  
  .priority-medium {
    border-left: 3px solid #faad14;
  }
  
  .priority-low {
    border-left: 3px solid #52c41a;
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 16px 12px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }

  /* Pagination styling - matching Member Records */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 32px 0 16px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          line-height: 28px !important;
        }
        
        .ant-select-arrow {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          right: 12px !important;
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 40px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          transition: all 0.3s ease !important;
          line-height: 28px !important;
          padding: 0 0 0 8px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
`;

export const ActionButton = styled(Button)`
  &.call-button {
    background: #52c41a;
    border-color: #52c41a;
    
    &:hover, &:focus {
      background: #73d13d;
      border-color: #73d13d;
    }
  }
  
  &.follow-up-button {
    background: #1890ff;
    border-color: #1890ff;
    
    &:hover, &:focus {
      background: #40a9ff;
      border-color: #40a9ff;
    }
  }
`;

export const MemberSelectionTable = styled(Table)`
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    white-space: nowrap !important;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    /* Even rows - gray background */
    &:nth-child(even) > td {
      background: #fafafa;
      
      &:hover {
        background: #f0f7ff !important;
      }
    }
    
    /* Ensure fixed columns inherit the row background */
    &:nth-child(even) .ant-table-cell-fix-right {
      background: #fafafa !important;
      
      &:hover {
        background: #f0f7ff !important;
      }
    }
    
    /* Odd rows (white background by default) */
    &:nth-child(odd) .ant-table-cell-fix-right {
      background: white !important;
      
      &:hover {
        background: #fafbff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 16px 12px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }

  /* Pagination styling - matching Member Records */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 32px 0 16px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          line-height: 28px !important;
        }
        
        .ant-select-arrow {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          right: 12px !important;
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 40px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          transition: all 0.3s ease !important;
          line-height: 28px !important;
          padding: 0 0 0 8px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
`;

export const FilterDrawerContent = styled.div`
  .filter-section {
    margin-bottom: 16px;
    
    .filter-label {
      font-size: 14px;
      font-weight: 500;
      color: ${COLORS.textDark};
      margin-bottom: 8px;
      display: block;
    }
  }
  
  .drawer-actions {
    display: flex;
    gap: 16px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid ${COLORS.border};
  }
`;

// CaseDetailsModal styled components
export const CaseDetailsModalContent = styled.div`
  .case-header {
    margin-bottom: 32px;
    
    .case-title {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
    }
    
    .case-id {
      color: ${COLORS.textMedium};
      font-size: 14px;
    }
  }
  
  .patient-info {
    background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
    padding: 24px;
    border-radius: 12px;
    margin-bottom: 28px;
    display: flex;
    align-items: center;
    gap: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid #e8e8e8;
    
    .patient-details {
      flex: 1;
      
      .patient-meta {
        margin-bottom: 12px;
        
        span {
          color: ${COLORS.textMedium};
          font-size: 14px;
          display: inline-flex;
          align-items: center;
        }
      }
      
      .case-badges {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 12px;
      }
    }
    
    .detail-item {
      flex: 1;
      min-width: 200px;
      
      .label {
        color: ${COLORS.textMedium};
        font-size: 12px;
        margin-bottom: 4px;
      }
      
      .value {
        color: ${COLORS.textDark};
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
  
  /* Ant Design Tabs spacing override */
  .ant-tabs {
    .ant-tabs-nav {
      margin-bottom: 24px;
      
      .ant-tabs-tab {
        padding: 12px 20px;
        font-size: 14px;
        
        &:hover {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
    
    .ant-tabs-content {
      padding-top: 8px;
    }
  }
  
  .timeline-item {
    display: flex;
    gap: 12px;
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 8px;
    transition: background 0.2s ease;
    
    &:hover {
      background: ${COLORS.backgroundLight};
    }
    
    .timeline-icon {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }
    
    .timeline-content {
      flex: 1;
    }
    
    .timeline-header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      margin-bottom: 4px;
    }
    
    .timeline-title {
      font-weight: 500;
      color: ${COLORS.textDark};
    }
    
    .timeline-time {
      color: ${COLORS.textMedium};
      font-size: 12px;
    }
    
    .timeline-description {
      color: ${COLORS.textMedium};
      font-size: 13px;
    }
  }
  
  .metric-card {
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-2px);
    }
    
    .metric-value {
      font-size: 28px;
      font-weight: 600;
      color: ${COLORS.textDark};
      margin-bottom: 8px;
    }
    
    .metric-label {
      color: ${COLORS.textMedium};
      font-size: 13px;
    }
  }
  
  .results-card {
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    transition: box-shadow 0.3s ease;
    
    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }
    
    .result-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 2px solid #f0f0f0;
    }
    
    .result-title {
      font-weight: 600;
      font-size: 15px;
      color: ${COLORS.textDark};
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .result-details {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 16px;
    }
    
    .result-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #f5f5f5;
      
      &:last-child {
        border-bottom: none;
      }
      
      .result-name {
        color: ${COLORS.textMedium};
        font-size: 14px;
        font-weight: 500;
      }
      
      .result-value {
        display: flex;
        align-items: center;
        gap: 12px;
        
        span {
          color: ${COLORS.textDark};
          font-size: 15px;
          font-weight: 600;
        }
      }
    }
  }
`;

export const CaseDetailsStatusTag = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  
  &.status-normal {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }
  
  &.status-elevated {
    background: #fff7e6;
    color: #fa8c16;
    border: 1px solid #ffd591;
  }
  
  &.status-high {
    background: #fff1f0;
    color: #ff4d4f;
    border: 1px solid #ffccc7;
  }
`;

// VoiceCallModal styled components
export const VoiceCallModalContent = styled.div`
  .call-status-header {
    text-align: center;
    padding: 24px;
    background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
    border-radius: 12px;
    margin-bottom: 28px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid #e8e8e8;
  }

  /* Modern call-progress hero */
  .call-hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 32px 24px 28px;
    background: linear-gradient(180deg, #fbfcfe 0%, #f4f7fb 100%);
    border: 1px solid #eef1f5;
    border-radius: 16px;
    margin-bottom: 20px;
  }

  .call-hero-avatar {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;

    /* Pulsing rings while dialing/ringing */
    &.pulse::before,
    &.pulse::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      border: 2px solid var(--ring-color, #1890ff);
      opacity: 0;
      animation: callPulse 1.8s ease-out infinite;
    }
    &.pulse::after {
      animation-delay: 0.9s;
    }

    /* Steady glow when connected */
    &.live::before {
      content: '';
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      box-shadow: 0 0 0 4px rgba(82, 196, 26, 0.15);
      animation: callGlow 1.6s ease-in-out infinite;
    }
  }

  @keyframes callPulse {
    0% { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(1.9); opacity: 0; }
  }

  @keyframes callGlow {
    0%, 100% { box-shadow: 0 0 0 4px rgba(82, 196, 26, 0.12); }
    50% { box-shadow: 0 0 0 10px rgba(82, 196, 26, 0.04); }
  }

  .call-hero-status {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }

  .call-hero-sub {
    margin-top: 6px;
    color: ${COLORS.textMedium};
    font-size: 14px;
    max-width: 380px;
  }

  .call-duration-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 6px 16px;
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    font-size: 15px;
    color: ${COLORS.$mainBlack};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #52c41a;
      animation: callGlow 1.6s ease-in-out infinite;
    }
  }

  /* Modernized setup step */
  .call-setup {
    .setup-patient-card {
      background: #fafbfc;
      border: 1px solid #eef0f2;
      border-radius: 14px;
      padding: 18px 20px;
    }

    .setup-patient-main {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .setup-patient-info {
      flex: 1;
      min-width: 0;
    }

    .setup-patient-name {
      font-size: 17px;
      font-weight: 700;
      color: ${COLORS.$mainBlack};
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .setup-patient-sub {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 6px;
    }

    .setup-patient-phone {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 500;
      color: ${COLORS.textMedium};
    }

    .setup-patient-muted {
      font-size: 13px;
      color: #faad14;
    }

    .setup-agent-note {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 20px;
      padding: 12px 16px;
      background: #f0f9ff;
      border: 1px solid #d6ebff;
      border-radius: 10px;
      font-size: 13px;
      color: #0050b3;
    }
  }
  
  .patient-info-card {
    margin-bottom: 24px;
    border-radius: 12px;
    
    .patient-header {
      display: flex;
      align-items: center;
      gap: 20px;
    }
    
    .patient-details {
      flex: 1;
    }
    
    .patient-name {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 6px;
    }
    
    .patient-meta {
      color: ${COLORS.textMedium};
      font-size: 14px;
    }
  }
  
  .call-controls {
    display: flex;
    justify-content: center;
    gap: 20px;
    padding: 24px 0;
  }
  
  .call-progress {
    margin: 24px 0;
  }
  
  .voice-agent-status {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border: 1px solid #bfdbfe;
    border-radius: 12px;
    padding: 20px;
    margin: 20px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-2px);
    }
    
    .agent-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }
    
    .agent-activity {
      color: ${COLORS.textMedium};
      font-size: 14px;
    }
  }
`;

export const TranscriptContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  max-height: 400px;
  overflow-y: auto;
  
  .transcript-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
    
    .transcript-title {
      font-weight: 600;
      font-size: 15px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .transcript-stats {
      display: flex;
      gap: 12px;
      font-size: 12px;
      
      .stat-item {
        display: flex;
        align-items: center;
        gap: 4px;
        color: ${COLORS.textMedium};
        
        .stat-value {
          font-weight: 600;
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  .response-time-banner {
    background: #f0f9ff;
    border: 1px solid #bae7ff;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 16px;
    text-align: center;
    font-size: 13px;
    color: #0050b3;
    
    .time-value {
      font-weight: 700;
      color: #1890ff;
    }
  }
  
  .transcript-messages {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .transcript-message {
    display: flex;
    flex-direction: column;
    animation: fadeIn 0.3s ease-in;
    max-width: 85%;
    
    /* iPhone iMessage blue bubbles for patient */
    &.user {
      align-self: flex-end;
      
      .message-bubble {
        background: #007AFF;
        color: white;
        padding: 10px 14px;
        border-radius: 18px;
        border-bottom-right-radius: 4px;
        font-size: 15px;
        line-height: 1.4;
        word-wrap: break-word;
      }
      
      .message-time {
        align-self: flex-end;
        font-size: 11px;
        color: #8e8e93;
        margin-top: 4px;
        margin-right: 4px;
      }
      
      .message-emotions {
        align-self: flex-end;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 6px;
        margin-right: 4px;
        
        .emotion-tag {
          font-size: 10px;
          padding: 3px 8px;
          background: rgba(0, 122, 255, 0.1);
          border: 1px solid rgba(0, 122, 255, 0.3);
          border-radius: 10px;
          color: #007AFF;
          font-weight: 500;
        }
      }
    }
    
    /* iPhone gray bubbles for Fred */
    &.assistant {
      align-self: flex-start;
      
      .message-bubble {
        background: #E9E9EB;
        color: #000000;
        padding: 10px 14px;
        border-radius: 18px;
        border-bottom-left-radius: 4px;
        font-size: 15px;
        line-height: 1.4;
        word-wrap: break-word;
      }
      
      .message-time {
        align-self: flex-start;
        font-size: 11px;
        color: #8e8e93;
        margin-top: 4px;
        margin-left: 4px;
      }
      
      .message-sender {
        font-size: 11px;
        color: #8e8e93;
        margin-bottom: 2px;
        margin-left: 4px;
        font-weight: 500;
      }
    }
    
    &.interruption {
      align-self: center;
      max-width: 100%;
      background: #fff1f0;
      border-left: 3px solid #ff4d4f;
      padding: 8px 12px;
      border-radius: 6px;
      text-align: center;
      font-style: italic;
      color: ${COLORS.textMedium};
      font-size: 13px;
    }
  }
  
  .transcript-empty {
    text-align: center;
    padding: 40px 20px;
    color: ${COLORS.textMedium};
    font-style: italic;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const VoiceCallButton = styled(Button)`
  &.start-call {
    background: #52c41a;
    border-color: #52c41a;
    height: 40px;
    font-size: 14px;
    
    &:hover, &:focus {
      background: #73d13d;
      border-color: #73d13d;
    }
  }
  
  &.end-call {
    background: #ff4d4f;
    border-color: #ff4d4f;
    height: 40px;
    font-size: 14px;
    
    &:hover, &:focus {
      background: #ff7875;
      border-color: #ff7875;
    }
  }
`;

// FollowUpModal styled components
export const FollowUpModalContent = styled.div`
  .follow-up-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
    padding: 20px 24px;
    border-radius: 12px;
    margin-bottom: 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid #e8e8e8;
    
    .patient-info {
      display: flex;
      align-items: center;
      gap: 16px;
    }
  }
  
  .form-section {
    margin-bottom: 28px;
    
    .section-title {
      color: ${COLORS.textDark};
      font-weight: 600;
      font-size: 15px;
      margin-bottom: 18px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }
  
  .quick-actions {
    margin-top: 12px;
    
    .ant-btn {
      margin-right: 8px;
      margin-bottom: 8px;
      border-radius: 8px;
    }
  }
  
  .reminder-options {
    margin-top: 12px;
    
    .ant-card {
      cursor: pointer;
      transition: all 0.3s ease;
      border-radius: 12px;
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
        transform: translateY(-2px);
      }
      
      &.selected {
        border-color: ${COLORS.$primaryBlue};
        background: #f0f8ff;
        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
      }
    }
  }
  
  .priority-cards {
    .priority-card {
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      border-radius: 12px;
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transform: translateY(-2px);
      }
      
      &.selected {
        border-width: 2px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }
    }
  }
  
  /* Ant Design Form overrides */
  .ant-form-item {
    margin-bottom: 20px;
  }
  
  .ant-input,
  .ant-select-selector,
  .ant-picker,
  .ant-input-textarea {
    border-radius: 8px;
    padding: 10px 14px;
  }
  
  .ant-radio-button-wrapper {
    border-radius: 8px;
    padding: 8px 20px;
    height: auto;
    
    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
    
    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
`;

// CallHistoryModal styled components
export const CallHistoryModalContent = styled.div`
  .call-stats {
    margin-bottom: 28px;
    
    .stat-card {
      text-align: center;
      padding: 20px;
      border-radius: 12px;
      border: 1px solid #e8e8e8;
      transition: all 0.3s ease;
      
      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transform: translateY(-2px);
      }
      
      .stat-value {
        font-size: 28px;
        font-weight: 600;
        color: ${COLORS.textDark};
      }
      
      .stat-label {
        color: ${COLORS.textMedium};
        font-size: 13px;
        margin-top: 8px;
      }
    }
  }
  
  .filters-section {
    background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
    padding: 20px 24px;
    border-radius: 12px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid #e8e8e8;
  }
  
  .call-row {
    .call-status {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .call-duration {
      font-family: 'Monaco', 'Courier New', monospace;
      font-weight: 500;
    }
    
    .call-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .audio-player {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
    
    .player-controls {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .waveform {
      flex: 1;
      height: 40px;
      background: linear-gradient(90deg, #1890ff 0%, #40a9ff 50%, #69c0ff 100%);
      border-radius: 4px;
      position: relative;
      overflow: hidden;
      
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 30%;
        height: 100%;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 4px;
      }
    }
  }
  
  .call-transcript {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
    max-height: 200px;
    overflow-y: auto;
    
    .transcript-line {
      margin-bottom: 8px;
      padding: 6px;
      border-radius: 4px;
      
      &.agent {
        background: #e6f7ff;
        border-left: 3px solid #1890ff;
      }
      
      &.patient {
        background: #f6ffed;
        border-left: 3px solid #52c41a;
      }
      
      .speaker {
        font-weight: 600;
        margin-bottom: 4px;
      }
      
      .timestamp {
        font-size: 11px;
        color: #666;
        float: right;
      }
    }
  }
`;

// Table cell styled components
export const PatientCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .patient-name {
    font-weight: 600;
    font-size: 14px;
  }

  .patient-id,
  .patient-phone {
    color: ${COLORS.textMedium};
    font-size: 12px;
  }
`;

export const RiskCell = styled.div`
  .risk-score {
    font-size: 11px;
    color: ${COLORS.textMedium};
  }
`;

export const ActionCell = styled.div`
  .action-title {
    font-weight: 500;
    margin-bottom: 4px;
  }

  .action-tag {
    font-size: 11px;
    margin-top: 2px;
  }
`;

export const CallAttemptsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .attempts-count {
    font-weight: 500;
  }
`;

export const LastContactCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ActionsDropdownButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: ${COLORS.backgroundLight};
    border-color: ${COLORS.$primaryBlue};
  }

  .anticon {
    font-size: 18px;
  }
`;

/* ============================================================
   Case Management (results call queue)
   ============================================================ */

export const QueueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  .qh-title {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.textDark};
    line-height: 1.3;
  }

  .qh-sub {
    color: ${COLORS.textMuted};
    font-size: 13px;
    margin-top: 2px;
  }

  .qh-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

export const ConfigChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  background: ${(p) => (p.$warn ? '#fff7e6' : '#E6FAF8')};
  color: ${(p) => (p.$warn ? '#ad6800' : COLORS.$deepTiffany)};
  border: 1px solid ${(p) => (p.$warn ? '#ffd591' : 'rgba(46, 139, 139, 0.25)')};
  white-space: nowrap;
`;

export const QueueStatCard = styled.button.attrs({ type: 'button' })`
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: 16px;
  border: 1.5px solid ${(p) => (p.$active ? '#1a1a1a' : '#f0f0f0')};
  background: #fff;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: ${(p) => (p.$active ? '0 6px 18px rgba(0, 0, 0, 0.10)' : '0 2px 8px rgba(0, 0, 0, 0.05)')};
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.$mainTiffany};
    outline-offset: 2px;
  }

  .qs-icon {
    width: 44px;
    height: 44px;
    min-width: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    color: #fff;
    background: ${(p) => p.$accent || '#1a1a1a'};
  }

  .qs-value {
    font-size: 24px;
    font-weight: 700;
    color: ${COLORS.textDark};
    line-height: 1.15;
  }

  .qs-label {
    font-size: 13px;
    color: ${COLORS.textMuted};
    font-weight: 500;
    white-space: nowrap;
  }
`;

export const QueueToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 14px;
  flex-wrap: wrap;

  .qt-search {
    flex: 1;
    min-width: 220px;
    height: 40px;
    border-radius: 10px;
  }
`;

export const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  .mc-avatar {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 50%;
    background: #1a1a1a;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .mc-name {
    font-weight: 600;
    color: ${COLORS.textDark};
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover { color: ${COLORS.$deepTiffany}; }
  }

  .mc-phone {
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const RiskPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  color: ${(p) => p.$color};
  background: ${(p) => p.$bg};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.$color};
  }
`;

export const ReasonCell = styled.div`
  font-size: 13px;
  color: ${COLORS.textMedium};
  line-height: 1.45;

  .rc-source {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.textDark};
  }

  .rc-finding {
    display: block;
    font-size: 12px;
    color: #a8071a;
  }

  .rc-time {
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const StepTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .st-line {
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: ${COLORS.border};
  }
`;

export const StepDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => (p.$state === 'done' ? COLORS.$deepTiffany : p.$state === 'live' ? '#0958d9' : p.$state === 'warn' ? '#ad6800' : COLORS.textMuted)};
  background: ${(p) => (p.$state === 'done' ? '#E6FAF8' : p.$state === 'live' ? '#e6f4ff' : p.$state === 'warn' ? '#fff7e6' : '#f5f5f5')};
`;

export const NextActionButton = styled(Button)`
  &&& {
    height: 36px;
    border-radius: 10px;
    font-weight: 600;
    min-width: 132px;
  }

  &&&.ant-btn-primary:not(:disabled) {
    background: #1a1a1a;
    border-color: #1a1a1a;

    &:hover {
      background: #333;
      border-color: #333;
    }
  }
`;

export const SmsPreview = styled.div`
  background: #f5f5f7;
  border-radius: 14px;
  padding: 16px;

  .sp-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${COLORS.textMuted};
    margin-bottom: 10px;
  }

  .sp-bubble {
    display: inline-block;
    max-width: 85%;
    background: #e9e9eb;
    color: #111;
    padding: 10px 14px;
    border-radius: 18px 18px 18px 4px;
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;
